import { useState, useEffect, useCallback, useMemo } from "react";
import { useClientApi } from "../../../hooks/useClientApi";
import { useClientIndustryApi } from "../../../hooks/useClientIndustryApi";
import { useCountryApi } from "../../../hooks/useCountryApi";
import { useCityApi } from "../../../hooks/useCityApi";
import { useLoading } from "../../../contexts/LoadingContext";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useClientDesignationApi } from "../../../hooks/useClientDesignationApi";
import { useRbac } from "../../../hooks/RbacContext";
export const useAllAccountsViewModel = () => {
  const [isAddClientOpen, setIsAddClientOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [successModal, setSuccessModal] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { checkAccess } = useRbac();
  const { showLoading, hideLoading } = useLoading();
  const [countryCities, setCountryCities] = useState([]);
  const [currentCountryId, setCurrentCountryId] = useState(null);

  const {
    getClients,
    deleteClient,
    createClient: apiCreateClient,
    updateClient: apiUpdateClient,
    cacheOperations: { readClients, writeClients },
  } = useClientApi();

  const {
    getClientIndustries,
    cacheOperations: {
      readClientIndustries: readIndustries,
      writeClientIndustries: writeIndustries,
    },
  } = useClientIndustryApi();

  const {
    getCountries,
    cacheOperations: { readCountries, writeCountries },
  } = useCountryApi();

  const {
    getCities,
    getCitiesByCountryId,
    cacheOperations: { readCities, writeCities },
  } = useCityApi();

  const {
    getClientDesignations,
    cacheOperations: { readClientDesignations, writeClientDesignations },
  } = useClientDesignationApi();

  const fetchCountryCities = useCallback(
    async (countryId) => {
      if (!countryId) {
        setCountryCities([]);
        return [];
      }

      if (currentCountryId === countryId && countryCities.length > 0) {
        return countryCities;
      }

      try {
        const result = await getCitiesByCountryId(countryId);
        if (result.success) {
          setCurrentCountryId(countryId);
          setCountryCities(result.data || []);
          return result.data;
        }
        setCountryCities([]);
        return [];
      } catch (error) {
        console.error("Error fetching cities:", error);
        setCountryCities([]);
        return [];
      }
    },
    [getCitiesByCountryId]
  );

  useEffect(() => {
    fetchData();
  }, []);

  const handleAddClient = useCallback(() => {
    setSelectedClient(null);
    setIsAddClientOpen(true);
  }, []);

  const getClientName = useMemo(
    () => (clientId) => {
      const client = readClients();

      const clientName = client.find(
        (clnt) => clnt.client_id.toString() === clientId?.toString()
      );

      return clientName?.client_name || "Unknown";
    },
    [readClients]
  );

  const handleEditClient = useCallback(
    (client) => {
      const pocs = Array.isArray(client.client_pocs) ? client.client_pocs : [];

      if (client.country_id) {
        fetchCountryCities(client.country_id.toString());
      }

      const clientWithPocs = {
        ...client,
        client_pocs: {
          poc_name: pocs.map((poc) => poc.poc_name || ""),
          poc_designation: pocs.map((poc) => poc.poc_designation || ""),
          poc_email: pocs.map((poc) => poc.poc_email || ""),
          poc_mobile: pocs.map((poc) => poc.poc_mobile || ""),
          poc_country_id: pocs.map(
            (poc) => poc.poc_country_id?.toString() || ""
          ),
          poc_business_unit: pocs.map((poc) => poc.poc_business_unit || ""),
          client_poc_id: pocs.map((poc) => poc.client_poc_id?.toString() || ""),
        },
      };

      setSelectedClient(clientWithPocs);
      setIsAddClientOpen(true);
    },
    [fetchCountryCities]
  );

  const handleCloseDialog = useCallback(() => {
    setIsAddClientOpen(false);
    setSelectedClient(null);
  }, []);

  const transformPocs = useCallback((formData, selectedClient) => {
    console.log("formData", formData);
    let transformedPocs = [];
    if (formData.client_pocs) {
      const {
        poc_name,
        poc_email,
        poc_country_id,
        client_poc_id,
        poc_designation,
        poc_mobile,
        poc_business_unit,
      } = formData.client_pocs;

      if (
        Array.isArray(poc_name) &&
        Array.isArray(poc_email) &&
        Array.isArray(poc_country_id)
      ) {
        if (
          poc_name.length !== poc_name.length ||
          poc_name.length !== poc_email.length
        ) {
          throw new Error("Invalid POC data format");
        }

        transformedPocs = poc_name.map((name, index) => {
          if (client_poc_id?.[index]) {
            return {
              client_poc_id: client_poc_id[index],
              poc_name: name,
              poc_email: poc_email[index].toLowerCase(),
              poc_country_id: parseInt(poc_country_id[index]),
              poc_designation: poc_designation[index],
              poc_mobile: poc_mobile[index],
              poc_business_unit: poc_business_unit[index],
            };
          }
          return {
            poc_name: name,
            poc_email: poc_email[index].toLowerCase(),
            poc_country_id: parseInt(poc_country_id[index]),
            poc_designation: poc_designation[index],
            poc_mobile: poc_mobile[index],
            poc_business_unit: poc_business_unit[index],
          };
        });
      }
    }
    return transformedPocs;
  }, []);

  const handleSubmit = useCallback(
    async (formData) => {
      console.log("formData", formData);
      try {
        showLoading();
        const transformedPocs = transformPocs(formData, selectedClient);
        const processedFormData = {
          ...formData,
          client_pocs: transformedPocs,
        };
        let result;
        if (selectedClient) {
          result = await apiUpdateClient(
            selectedClient.client_id,
            processedFormData
          );
        } else {
          result = await apiCreateClient(processedFormData);
        }

        if (!result.success) {
          // setSuccessModal({
          //   type: "error",
          //   title: "Action Failed",
          //   message: result.error,
          // });
          return { success: false, error: result.error };
        }

        setSuccessModal({
          type: "success",
          title: "Success",
          message: selectedClient
            ? "Client information updated successfully."
            : "Client registered successfully.",
        });
        handleCloseDialog();
        return { success: true };
      } catch (error) {
        console.log("228---------------------");
        setSuccessModal({
          type: "error",
          title: "Action Failed",
          message: error.message || "An error occurred",
        });
        return { success: false, error: error.message };
      } finally {
        hideLoading();
      }
    },
    [
      selectedClient,
      apiUpdateClient,
      apiCreateClient,
      showLoading,
      hideLoading,
      handleCloseDialog,
      transformPocs,
    ]
  );

  const fetchData = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    setError(null);

    try {
      const [
        clientsResult,
        industriesResult,
        countriesResult,
        citiesResult,
        designationsResult,
      ] = await Promise.all([
        getClients("all"),
        getClientIndustries(),
        getCountries(),
        getCities(),
        getClientDesignations(),
      ]);
      if (
        !clientsResult.success ||
        !industriesResult.success ||
        !countriesResult.success ||
        !designationsResult.success
      ) {
        throw new Error("Failed to fetch required data");
      }
      writeClients(clientsResult.data);
      writeIndustries(industriesResult.data);
      writeCountries(countriesResult.data);
      writeCities(citiesResult.data);
      writeClientDesignations(designationsResult.data);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  }, [
    getClients,
    getClientIndustries,
    getCountries,
    getCities,
    getClientDesignations,
    writeClients,
    writeIndustries,
    writeCountries,
    writeCities,
    writeClientDesignations,
  ]);

  useEffect(() => {
    fetchData();
  }, []);

  const handleDelete = useCallback(
    async (client_id) => {
      try {
        const result = await deleteClient(client_id);
        if (result.success) {
          setSuccessModal("Client deleted successfully");
        } else {
          setError(result.error);
        }
      } catch (err) {
        setError(err.message);
        console.error("Error deleting client:", err);
      }
    },
    [deleteClient]
  );

  const getIndustryName = useMemo(
    () => (id) => {
      const industries = readIndustries();
      const industry = industries.find(
        (ind) => ind.client_industry_id.toString() === id?.toString()
      );
      return industry?.industry_name || "Unknown";
    },
    [readIndustries]
  );

  const getCountryName = useMemo(
    () => (countryId) => {
      const countries = readCountries();
      const country = countries.find(
        (c) => c.country_id.toString() === countryId?.toString()
      );
      return country?.country_name || "Unknown";
    },
    [readCountries]
  );

  const getCityName = useMemo(
    () => (cityId) => {
      const cities = readCities();
      const city = cities.find(
        (c) => c.city_id.toString() === cityId?.toString()
      );
      return city?.city_id || "Unknown";
    },
    [readCities]
  );

  const getActions = useCallback(
    (client) => {
      const actions = [];

      if (checkAccess("client", "update")) {
        actions.push({
          icon: <EditIcon fontSize="small" />,
          label: "Update",
          onClick: () => handleEditClient(client),
          color: "primary",
          disabled: client?.is_default,
          sx: {
            "&:hover": {
              backgroundColor: (theme) =>
                theme.palette.primary.lighter || "rgba(33, 150, 243, 0.08)",
            },
          },
        });
      }
      if (checkAccess("client", "delete")) {
        actions.push({
          icon: <DeleteIcon fontSize="small" />,
          label: "Delete",
          onClick: () => setDeleteConfirmation({ client }),
          color: "error",
          disabled: client?.is_default,
          sx: {
            "&:hover": {
              backgroundColor: (theme) =>
                theme.palette.error.lighter || "rgba(244, 67, 54, 0.08)",
            },
          },
        });
      }

      return actions;
    },
    [handleEditClient, checkAccess, setDeleteConfirmation]
  );
  console.log("clientDesignations", readClientDesignations());

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteConfirmation?.client) return;

    try {
      showLoading();
      const result = await deleteClient(deleteConfirmation.client.client_id);

      if (result.success) {
        setSuccessModal({
          type: "success",
          title: "Success",
          message: "Client deleted successfully",
        });
      } else {
        setSuccessModal({
          type: "error",
          title: "Action Failed",
          message: result.error || "Failed to delete client",
        });
      }
    } catch (error) {
      setSuccessModal({
        type: "error",
        title: "Action Failed",
        message: error.message || "An error occurred while deleting the client",
      });
    } finally {
      hideLoading();
      setDeleteConfirmation(null);
    }
  }, [
    deleteConfirmation,
    deleteClient,
    showLoading,
    hideLoading,
    readClients,
    writeClients,
  ]);

  const handleDeleteClick = useCallback((client) => {
    setDeleteConfirmation({
      client,
      title: "Delete Client",
      message: `Are you sure you want to delete client "${client.client_name}"?`,
    });
  }, []);

  return {
    isAddClientOpen,
    selectedClient,
    successModal,
    deleteConfirmation,
    loading,
    error,
    clients: readClients(),
    industries: readIndustries(),
    countries: readCountries(),
    cities: readCities(),
    clientDesignations: readClientDesignations(),
    handleAddClient,
    handleEditClient,
    handleCloseDialog,
    handleSubmit,
    setSuccessModal,
    setDeleteConfirmation,
    handleDelete,
    getIndustryName,
    getCountryName,
    getCityName,
    getActions,
    getClientName,
    refetch: fetchData,
    fetchCountryCities,
    countryCities,
    handleDeleteConfirm,
    handleDeleteClick,
  };
};
